import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/lib/brand";
import { trackEvent } from "@/lib/analytics";

const INTENTS = [
    { key: "stress", label: "stress" },
    { key: "loneliness", label: "loneliness" },
    { key: "anxiety", label: "anxiety" },
    { key: "sleep", label: "sleep" },
    { key: "reflection", label: "reflection" },
    { key: "grief", label: "grief" },
    { key: "curious", label: "just curious" },
];

const LOCALES = [
    { key: "en", label: "English" },
    { key: "hi", label: "हिंदी" },
];

/**
 * Shown on first Companion visit if the user hasn't onboarded yet.
 * Two soft questions: what should Pax call you, what brings you here + language.
 */
export default function Onboarding() {
    const { user, refresh } = useAuth();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [preferredName, setPreferredName] = useState("");
    const [intent, setIntent] = useState(null);
    const [locale, setLocale] = useState("en");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        if (user.onboarded_at) return;
        setPreferredName(user.preferred_name || user.name || "");
        setLocale(user.locale || "en");
        setOpen(true);
        trackEvent("onboarding_shown");
    }, [user]);

    const next = () => {
        if (step === 0 && !preferredName.trim()) {
            toast.error("what should Pax call you?");
            return;
        }
        setStep(step + 1);
    };

    const finish = async () => {
        setBusy(true);
        try {
            await api.post("/onboarding", {
                preferred_name: preferredName.trim(),
                intent,
                locale,
            });
            trackEvent("onboarding_completed", { intent, locale });
            await refresh?.();
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "couldn't save. try again?");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-[3px] p-4"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    data-testid="onboarding-backdrop"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                        transition={{ type: "spring", damping: 22, stiffness: 260 }}
                        className="bg-bone rounded-3xl border border-clayHover shadow-clay w-full max-w-lg p-6 sm:p-8"
                        data-testid="onboarding-modal"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <img src={BRAND.logo} alt="Pax" className="w-10 h-10 object-contain" />
                            <div>
                                <p className="text-xs uppercase tracking-[0.25em] text-inkSoft font-body">arriving</p>
                                <h2 className="font-heading text-2xl tracking-tight text-ink">let's meet properly.</h2>
                            </div>
                        </div>

                        {step === 0 && (
                            <div>
                                <label className="block font-body text-inkSoft text-sm mb-2">
                                    what should Pax call you?
                                </label>
                                <input
                                    autoFocus
                                    value={preferredName}
                                    onChange={(e) => setPreferredName(e.target.value)}
                                    placeholder="a name, a nickname, anything…"
                                    className="w-full bg-white border border-clayHover rounded-2xl px-4 py-3 outline-none focus:border-terracotta font-body text-ink"
                                    data-testid="onboarding-name"
                                    maxLength={60}
                                    onKeyDown={(e) => e.key === "Enter" && next()}
                                />
                                <button
                                    onClick={next}
                                    data-testid="onboarding-next"
                                    className="mt-6 w-full bg-terracotta text-bone px-5 py-3 rounded-full hover:bg-terracottaHover transition-colors font-body"
                                >
                                    continue
                                </button>
                            </div>
                        )}

                        {step === 1 && (
                            <div>
                                <label className="block font-body text-inkSoft text-sm mb-3">
                                    what brings you here today? <span className="opacity-60">(optional)</span>
                                </label>
                                <div className="flex flex-wrap gap-2 mb-6" data-testid="onboarding-intents">
                                    {INTENTS.map((i) => (
                                        <button
                                            key={i.key}
                                            onClick={() => setIntent(intent === i.key ? null : i.key)}
                                            data-testid={`onboarding-intent-${i.key}`}
                                            className={`px-4 py-2 rounded-full font-body text-sm transition-all border ${
                                                intent === i.key
                                                    ? "bg-terracotta text-bone border-terracotta"
                                                    : "bg-white text-inkSoft border-clayHover hover:border-terracotta hover:text-ink"
                                            }`}
                                        >
                                            {i.label}
                                        </button>
                                    ))}
                                </div>

                                <label className="block font-body text-inkSoft text-sm mb-3">
                                    Pax's language
                                </label>
                                <div className="flex flex-wrap gap-2 mb-8" data-testid="onboarding-locales">
                                    {LOCALES.map((l) => (
                                        <button
                                            key={l.key}
                                            onClick={() => setLocale(l.key)}
                                            data-testid={`onboarding-locale-${l.key}`}
                                            className={`px-4 py-2 rounded-full font-body text-sm transition-all border ${
                                                locale === l.key
                                                    ? "bg-ink text-bone border-ink"
                                                    : "bg-white text-inkSoft border-clayHover hover:border-ink hover:text-ink"
                                            }`}
                                        >
                                            {l.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setStep(0)}
                                        className="text-inkSoft hover:text-ink px-3 py-3 font-body text-sm"
                                    >
                                        back
                                    </button>
                                    <button
                                        onClick={finish}
                                        disabled={busy}
                                        data-testid="onboarding-finish"
                                        className="flex-1 bg-terracotta text-bone px-5 py-3 rounded-full hover:bg-terracottaHover disabled:opacity-60 transition-colors font-body"
                                    >
                                        {busy ? "saving…" : "begin with Pax"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
